import { type User, type ApiResponse } from '../types';
import { request, setToken, clearToken, getToken } from './http';
import { cacheGet, cacheSet } from './cache';

interface LoginData {
	username: string;
	password: string;
}

interface AuthPayload {
	user: User;
	token: string;
}

const signup = async ({ username, password }: LoginData): Promise<User> => {
	const response = await request<ApiResponse<AuthPayload>>('/auth/signup', {
		method: 'POST',
		body: JSON.stringify({ username, password })
	});
	setToken(response.data.token);
	cacheSet('user', response.data.user, true);
	return response.data.user;
};

const login = async ({ username, password }: LoginData): Promise<User> => {
	const response = await request<ApiResponse<AuthPayload>>('/auth/login', {
		method: 'POST',
		body: JSON.stringify({ username, password })
	});
	setToken(response.data.token);
	cacheSet('user', response.data.user, true);
	return response.data.user;
};

const logout = async (): Promise<void> => {
	clearToken();
	// Fire-and-forget; auth is stateless so the server has nothing to clear
	try {
		await request<void>('/auth/logout', { method: 'POST', body: JSON.stringify({}) });
	} catch {
		// token is already gone locally — nothing to recover
	}
};

const me = async (): Promise<User> => {
	const response = await request<ApiResponse<User>>('/auth/me');
	cacheSet('user', response.data, true);
	return response.data;
};

/** The account from the last visit, for painting before /auth/me answers. */
const cachedUser = (): User | null => (getToken() ? cacheGet<User>('user') ?? null : null);

const hasSession = (): boolean => getToken() !== null;

const auth = { signup, login, logout, me, hasSession, cachedUser };

export default auth;
